import clsx from "clsx";
import React, { ReactNode, ReactElement, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import TawkMessengerReact from "@tawk.to/tawk-messenger-react";
import Cookies from "js-cookie";

import {
  UserRole,
  RequiresUpgradeReason,
  Plan,
  planClients,
  talkToConfig,
  PlanTier,
  LocalStorageKeys,
  IsMobileAppModalShown,
  REACTIVATE_PROMO_CANCEL_BEFORE_DATE,
  REACTIVATE_PROMO_QUERY,
  planStatus,
  CONFIRM_SIGNUP_SUBSCRIPTION_QUERY,
  SignupCheckoutStatus,
} from "../../constants";
import { AppSpeedDial } from "../speed-dial/AppSpeedDial";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useQueryParam } from "../../hooks/useQueryParam";
import {
  useCurrentUser,
  useUserIsClient,
  useUserIsLapsedCoach,
} from "../../hooks/useCurrentUser";
import { useMediaMobile } from "../../hooks/useMediaMobile";
import { getRequiresUpgrade } from "../../utils/user";
import { PreviewType, PreviewContext } from "../../hooks/usePreview";
import { getTodayDaysDiff } from "../../utils/date";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "./AppBar";
import AppDrawer from "./AppDrawer";
import { AppTabsNavigation } from "./AppTabsNavigation";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { UpgradeBanner } from "./UpgradeBanner";
import { YearInReviewBanner } from "./YearInReviewBanner";
import { polyfillCSS } from "../../utils/css";
import {
  CLIENT_SUBSCRIPTION_CANCELED_ROUTE,
  CLIENT_TRIAL_EXPIRED_ROUTE,
  COACH_CLIENTS_ACTIVE_ROUTE,
  COACH_PLAN_ROUTE,
  LOGOUT_ROUTE,
  PREVIEW_ROUTE,
} from "../../routes/routes";
import { AppDrawerContext } from "../../hooks/useDrawer";
import MinimizedDrawerContext, {
  getLocalStorageMinimizedDrawerValue,
} from "../../contexts/MinimizedDrawerContext";
import BannersContext, { BannerType } from "../../contexts/BannersContext";
import { DownloadMobileAppDialog } from "../dialog/app/DownloadMobileAppDialog";
import { useMobileAppModalContextData } from "../../contexts/MobileAppModalContext";
import { isMobileApp, iOSMobileApp } from "../../utils/mobile";
import { useCurrentBrand } from "../../hooks/useCurrentWorkspace";
import { isLocalEnvironment } from "../../utils";
import { UpdateMobileAppDialog } from "../dialog/app/UpdateMobileAppDialog";
import { useCurrentAppVersion } from "../../hooks/useAppVersion";
import { useMainenanceMode } from "../../hooks/useMaintenanceMode";
import { MaintenanceModeDialog } from "../dialog/app/MaintenanceModeDialog";
import { ReactivateCoachDialog } from "../dialog/app/ReactivateCoachDialog";
import { CompleteSignupBillingDialog } from "../dialog/app/CompleteSignupBillingDialog";
import {
  ReconnectAppleHealthDialog,
  HIDE_RECONNECT_APPLE_HEALTH_DIALOG_KEY,
} from "../dialog/app/ReconnectAppleHealthDialog";
import GetAnyHelpModalContext from "../../contexts/GetAnyHelpModalContext";
import { GetAnyHelpModal, InfoItemName } from "../menu/GetAnyHelpModal";
import { JourneyAvailabilityContext } from "../../contexts/JourneyAvailabilityContext";
import { SplashScreen } from "@capacitor/splash-screen";
import { useWebSpaChangesNotification } from "../dialog/app/useWebSpaChangesNotification";
import { useToastAlert } from "./ToastAlert/ToastAlertProvider";
import { PullRefresh } from "../pull-refresh/PullRefresh";
import { WEB_SPA_VERSION } from "../..";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { terraCapacitorEnabled } from "../../utils/terra-sdk";
import {
  COACH_MESSAGES_ROUTE,
  CLIENT_MESSAGE_ROUTE,
  COACH_CLIENTS_ROUTE,
  COACH_BRANDED_APP_ROUTE,
  CLIENT_HOME_ROUTE,
  BRANDED_LOGIN_ROUTE,
} from "../../routes/routes";
import { useAppMetadata } from "../../hooks/useAppMetadata";

const useStyles = (appDrawer: boolean, minimizedDrawer: boolean) =>
  makeStyles((theme) => ({
    "@global": {
      "#root": {
        [theme.breakpoints.up("llg")]: {
          paddingLeft: polyfillCSS(
            appDrawer
              ? `calc(${minimizedDrawer ? theme.drawer.minimizedWidth : theme.drawer.width}px + var(--safe-area-inset-left))`
              : "0px",
          ),
          transition: theme.transitions.create("padding-left", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      },
    },
    white: {
      backgroundColor: theme.palette.common.white,
    },
    divider: {
      [theme.breakpoints.up("md")]: {
        paddingBottom: 24,
      },
    },
  }));

type Props = {
  appBar?: boolean;
  appDrawer?: boolean;
  appBarHideOnMobile?: boolean;
  children?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: Array<ReactElement>;
  breadcrumbs?: any[];
  tabsNavigation?: any[];
  filters?: any;
  trackInfo?: {
    name?: string;
    properties?: Object;
  };
  cover?: string;
  disableSpeedDial?: boolean;
  forceBackButton?: boolean;
  hideUpgradeBanner?: boolean;
  tabNavigationStyles?: string;
};

const HIDE_HELP_CENTER_COACH_ROUTES = [
  COACH_MESSAGES_ROUTE,
  COACH_CLIENTS_ROUTE,
  COACH_BRANDED_APP_ROUTE,
  BRANDED_LOGIN_ROUTE,
];

export function AppLayout(props: Props) {
  const {
    appDrawer = true,
    appBar = true,
    appBarHideOnMobile,
    title,
    subtitle,
    actions,
    breadcrumbs,
    tabsNavigation,
    filters,
    trackInfo,
    cover,
    disableSpeedDial = true,
    forceBackButton = false,
    hideUpgradeBanner = false,
    tabNavigationStyles,
    children,
  } = props;
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [preview, setPreview] = React.useState<PreviewType>(null);

  const [minimizedDrawer, setMinimizedDrawer] = useState(
    getLocalStorageMinimizedDrawerValue(),
  );

  const [availability, setAvailability] = useState<{
    enabled: boolean;
    disabledReason?: string;
  }>({ enabled: true });

  const { notify, closeToastAlert } = useWebSpaChangesNotification();
  const [, trackPage] = useAnalytics();
  const [queryPreview] = useQueryParam("preview");
  const [queryReactivated] = useQueryParam(REACTIVATE_PROMO_QUERY);
  const [querySignupSubscription] = useQueryParam(
    CONFIRM_SIGNUP_SUBSCRIPTION_QUERY,
  );

  const user = useCurrentUser();
  const location = useLocation();
  const { pathname } = useLocation();
  const [terraConnected] = useLocalStorage<boolean>("terra-connected", false);
  const [dismissedReconnectDialog] = useLocalStorage<boolean>(
    HIDE_RECONNECT_APPLE_HEALTH_DIALOG_KEY,
    false,
  );
  const hasAppleProvider =
    user?.terraProviders?.some(
      (provider) => provider.provider?.toLowerCase() === "apple",
    ) ?? false;
  const shouldShowReconnectDialog =
    terraCapacitorEnabled &&
    hasAppleProvider &&
    !terraConnected &&
    !dismissedReconnectDialog &&
    iOSMobileApp;
  const isPreviewRoute =
    queryPreview === "true" || location.pathname.includes(PREVIEW_ROUTE);

  const isReactivatedSubscriptionRoute =
    queryReactivated === "success" ||
    location.pathname.includes(COACH_CLIENTS_ACTIVE_ROUTE);

  const confirmSubscriptionRoute =
    querySignupSubscription === SignupCheckoutStatus.SUCCEEDED ||
    querySignupSubscription === SignupCheckoutStatus.FAILED;

  const {
    isBrandedApp,
    isBrandedAppPublished,
    isIosBrandedAppPublished,
    isAndroidBrandedAppPublished,
  } = useCurrentBrand();

  const { yearInReviewMode, yearInReviewCoachesList } = useAppMetadata() || {};

  const isYearInReviewShown =
    yearInReviewMode === "enabled"
    || (yearInReviewMode === "adminOnly"
      && (user?.admin || user?.isImpersonating))
    || (yearInReviewMode === "definedCoaches"
      && user?.id != null
      && Array.isArray(yearInReviewCoachesList)
      && yearInReviewCoachesList.includes(user?.id));

  const { showToastAlert } = useToastAlert();

  // Use to show toast messages from the location state
  // (e.g. after redirecting from a different route)
  const { toastMessage } = location.state || "";

  React.useEffect(() => {
    if (toastMessage && toastMessage?.trim().length > 0) {
      showToastAlert("info", {
        message: toastMessage,
      });
    }
  }, [toastMessage]);

  const s = useStyles(
    !isPreviewRoute && appBar ? appDrawer : false,
    minimizedDrawer,
  )();
  const isMobile = useMediaMobile("md", false);
  const trialDaysDiff = React.useMemo(
    () =>
      user?.trialExpiryDate
        ? getTodayDaysDiff(user?.trialExpiryDate?.toString())
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.trialExpiryDate],
  );

  const trialExpired = user?.trialExpired ?? false;

  // NOTE: Coach considered to be `new` if he was created less subscription trial days (14)
  const isNewCoach =
    user?.role === UserRole.COACH &&
    Math.abs(user.created.diff(new Date(), "days")) < 14;

  const [requiresUpgrade, reason] = getRequiresUpgrade(
    trialExpired,
    user?.plan as Plan,
    user?.subscription?.status,
  );
  const [visibleUpgradeBanner, setVisibleUpgradeBanner] = React.useState(true);

  const validStatuses = ["active", "trialing", "incomplete", "past_due"];

  const [verifySubscriptionDialogType, setVerifySubscriptionDialogType] =
    useState<SignupCheckoutStatus | null>(null);

  // TODO_SIGNUP: Move to separate hook
  useEffect(() => {
    if (verifySubscriptionDialogType !== null) return;
    if (confirmSubscriptionRoute) {
      setVerifySubscriptionDialogType(
        querySignupSubscription as SignupCheckoutStatus,
      );
    } else if (user && user.subscription == null) {
      setVerifySubscriptionDialogType(SignupCheckoutStatus.INCOMPLETE);
    } else {
      setVerifySubscriptionDialogType(null);
    }
  }, [user, querySignupSubscription]);

  React.useEffect(() => {
    if (!user?.admin) {
      if (
        user &&
        !validStatuses.includes(user?.subscription?.status) &&
        user.role === UserRole.COACH
      ) {
        if (
          location.pathname !== LOGOUT_ROUTE &&
          !/\/coach\/card-information/.test(location.pathname) &&
          !isReactivatedSubscriptionRoute &&
          // new coaches should see the `CompleteSignupBillingDialog` instead
          !(isNewCoach && user.subscription == null)
        ) {
          navigate(COACH_PLAN_ROUTE);
        }
      }
      if (user && !user.isImpersonating) {
        if (requiresUpgrade) {
          if (user.role === UserRole.COACH) {
            if (
              location.pathname !== LOGOUT_ROUTE &&
              !/\/coach\/card-information/.test(location.pathname) &&
              !isReactivatedSubscriptionRoute &&
              // new coaches should see the `CompleteSignupBillingDialog` instead
              !(isNewCoach && user.subscription == null)
            ) {
              navigate(COACH_PLAN_ROUTE);
            }
          }

          if (
            user.role === UserRole.CLIENT &&
            !/\/signup\/client/.test(location.pathname)
          ) {
            navigate(
              reason === RequiresUpgradeReason.TRAIL_EXPIRED
                ? CLIENT_TRIAL_EXPIRED_ROUTE
                : CLIENT_SUBSCRIPTION_CANCELED_ROUTE,
              { replace: true },
            );
          }
        }
      }
    }
  }, [location.pathname, reason, requiresUpgrade, user]);

  React.useEffect(() => {
    // Ensures preview state is turned off on history change
    if (preview) {
      const listener = () => {
        setPreview(null);
      };

      window.addEventListener("popstate", listener);

      return () => {
        window.removeEventListener("popstate", listener);
      };
    }
  }, [preview]);

  if (typeof title === "string") {
    window.document.title = title;
  }

  if (trackInfo) {
    trackPage(trackInfo.name, trackInfo.properties);
  }

  const toggleDrawer = React.useCallback(() => {
    setDrawerOpen((x) => !x);
  }, []);

  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const showUpgradeBanner = React.useMemo(
    () =>
      (user?.role === UserRole.COACH &&
        !user?.isImpersonating &&
        !user?.subscription &&
        !isNewCoach &&
        !hideUpgradeBanner &&
        !preview &&
        !queryPreview) ||
      ((user?.subscription?.status === planStatus.CANCELED ||
        user?.subscription?.status === planStatus.PAST_DUE ||
        user?.subscription?.status === planStatus.INCOMPLETE) &&
        user?.role === UserRole.COACH),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideUpgradeBanner, preview, queryPreview, trialDaysDiff, user],
  );

  // TODO STR-2128: Drop AppCues
  // useEffect(() => {
  //   if (user) {
  //     window.Appcues?.identify(user.externalReferenceId);
  //   }
  // }, [user]);

  const isImpersonatingBannerShown = user?.isImpersonating;
  const isUpgradeBannerShown =
    !isPreviewRoute && showUpgradeBanner && visibleUpgradeBanner;
  const isYearInReviewBannerShown = true;

  const isAnyBannerShown = isImpersonatingBannerShown || isUpgradeBannerShown || isYearInReviewBannerShown;

  const getBannerType = () => {
    if (isImpersonatingBannerShown) return BannerType.IMPERSONATION;
    if (isUpgradeBannerShown) return BannerType.UPGRADE;
    if (isYearInReviewBannerShown) return BannerType.YEAR_IN_REVIEW;
  };

  const [isDownloadAppDialogOpen, setIsDownloadAppDialogOpen] =
    useMobileAppModalContextData();
  const onCloseDownloadAppDialog = () => {
    setIsDownloadAppDialogOpen(false);
    window.localStorage.setItem(IsMobileAppModalShown, "false");
  };
  const maintenanceMode = useMainenanceMode();
  const { requiresUpdate, requiresForceUpdate, hasNewSpaVersion } =
    useCurrentAppVersion();

  const [isMaintenanceEnabled, setIsMaintenanceEnabled] = useState<boolean>(
    maintenanceMode.enabled,
  );

  const [isUpdateAppDialogOpen, setIsUpdateAppDialogOpen] = useState<boolean>(
    requiresUpdate || requiresForceUpdate,
  );

  const onCloseUpdateAppDialog = () => {
    setIsUpdateAppDialogOpen(false);
  };

  const isLapsedCoach = useUserIsLapsedCoach();

  const openReactivatedDialog =
    !isNewCoach &&
    !isReactivatedSubscriptionRoute &&
    !confirmSubscriptionRoute &&
    user != null &&
    user?.role === UserRole.COACH &&
    isLapsedCoach;

  const [isReactivationDialogOpen, setIsReactivationDialogOpen] =
    useState<boolean>(openReactivatedDialog);

  useEffect(() => {
    setIsUpdateAppDialogOpen(requiresUpdate);
  }, [requiresUpdate]);

  useEffect(() => {
    setIsUpdateAppDialogOpen(requiresForceUpdate);
  }, [requiresForceUpdate]);

  useEffect(() => {
    setIsMaintenanceEnabled(maintenanceMode.enabled);
  }, [maintenanceMode]);

  useEffect(() => {
    let interval;
    let initId = null;
    let intervalId = null;
    if (hasNewSpaVersion) {
      initId = notify();
      interval = setInterval(
        () => {
          closeToastAlert(intervalId);
          intervalId = notify();
          closeToastAlert(initId);
        },
        5 * 60 * 1000,
      ); // Notify every 5 minutes
    } else {
      clearInterval(interval);
    }

    const SPA_SHOW_VERSION_COOKIE_NAME = "SPA_SHOW_VERSION_COOKIE_NAME";
    const showVersion = Cookies.get(SPA_SHOW_VERSION_COOKIE_NAME);

    if (showVersion === "true") {
      console.log("Current SPA version:", WEB_SPA_VERSION);
      showToastAlert("info", {
        message: `Current SPA version: ${WEB_SPA_VERSION}. Has new version: ${hasNewSpaVersion}`,
      });
    }

    return () => {
      clearInterval(interval);
    };
  }, [hasNewSpaVersion]);

  const isClient = useUserIsClient();
  const isStridistMobileApp = !isBrandedApp && isMobileApp;
  const forceClientToDownloadBrandedApp =
    isStridistMobileApp &&
    isBrandedAppPublished &&
    ((iOSMobileApp && isIosBrandedAppPublished) ||
      (!iOSMobileApp && isAndroidBrandedAppPublished));

  const [getAnyHelpModal, setGetAnyHelpModal] = useState({
    open: false,
    items: null,
  });

  const handleSetGetAnyHelpModal = (open: boolean, items?: InfoItemName[]) => {
    setGetAnyHelpModal({ open, items });
  };

  const handleRefresh = () => {
    const promise = SplashScreen.show({
      showDuration: 1000,
      autoHide: true,
    });
    window.location.reload();
    return promise;
  };

  const shouldShowHelpCenter = React.useMemo(() => {
    const isHiddenRoute = HIDE_HELP_CENTER_COACH_ROUTES.some((r) =>
      pathname.includes(r),
    );

    return (
      !isClient &&
      !isPreviewRoute &&
      !isMobile &&
      !user?.isImpersonating &&
      !isBrandedApp &&
      !isHiddenRoute
    );
  }, [pathname, user, isClient, isMobile, isBrandedApp, isPreviewRoute]);

  // Ensure the Tawk widget visibility updates dynamically whenever `shouldShowHelpCenter` changes.
  // This handles cases where the widget may already be loaded and avoids re-rendering the component.
  useEffect(() => {
    if (window.Tawk_API) {
      if (shouldShowHelpCenter) {
        window.Tawk_API.showWidget?.();
      } else {
        window.Tawk_API.hideWidget?.();
      }
    }
  }, [shouldShowHelpCenter]);

  return (
    <JourneyAvailabilityContext.Provider
      value={{
        ...availability,
        setAvailability,
      }}
    >
      <BannersContext.Provider
        value={{ bannerType: getBannerType(), isBannerShown: isAnyBannerShown }}
      >
        <AppDrawerContext.Provider value={[drawerOpen, setDrawerOpen]}>
          <MinimizedDrawerContext.Provider
            value={{ minimizedDrawer, setMinimizedDrawer }}
          >
            <PreviewContext.Provider value={[preview, setPreview]}>
              <GetAnyHelpModalContext.Provider
                value={{
                  getAnyHelpModal: getAnyHelpModal.open,
                  setGetAnyHelpModal: handleSetGetAnyHelpModal,
                }}
              >
                {isMobileApp && <PullRefresh onRefresh={handleRefresh} />}
                {!preview && (
                  <>
                    <DownloadMobileAppDialog
                      force={forceClientToDownloadBrandedApp}
                      open={
                        ((isDownloadAppDialogOpen && !isMobileApp) ||
                          forceClientToDownloadBrandedApp) &&
                        isClient
                      }
                      onClose={onCloseDownloadAppDialog}
                    />
                    <UpdateMobileAppDialog
                      force={requiresForceUpdate}
                      open={!isMaintenanceEnabled && isUpdateAppDialogOpen}
                      onClose={onCloseUpdateAppDialog}
                    />
                    <MaintenanceModeDialog open={isMaintenanceEnabled} />
                    <CompleteSignupBillingDialog
                      open={isNewCoach && verifySubscriptionDialogType != null}
                      onClose={() => setVerifySubscriptionDialogType(null)}
                      type={verifySubscriptionDialogType}
                    />
                    <ReactivateCoachDialog open={isReactivationDialogOpen} />
                    {shouldShowReconnectDialog && (<ReconnectAppleHealthDialog
                      force={false}
                      open={shouldShowReconnectDialog}
                      onClose={() => { }}
                    />)}

                    {!disableSpeedDial && <AppSpeedDial />}
                    {isImpersonatingBannerShown && <ImpersonationBanner />}
                    {isUpgradeBannerShown && (
                      <UpgradeBanner
                        status={user?.subscription?.status}
                        trialEndsInDays={trialDaysDiff}
                        setVisibleUpgradeBanner={setVisibleUpgradeBanner}
                      />
                    )}
                    {(isYearInReviewShown && user?.role === UserRole.COACH) && (
                      <YearInReviewBanner />
                    )}
                    {appBar && (
                      <>
                        {appDrawer && (
                          <AppDrawer open={drawerOpen} onClose={closeDrawer} />
                        )}
                        <AppBar
                          title={title}
                          subtitle={subtitle}
                          actions={actions}
                          breadcrumbs={breadcrumbs}
                          onMenuClick={toggleDrawer}
                          hideOnMobile={appBarHideOnMobile}
                          forceBackButton={forceBackButton}
                          navItems={tabsNavigation}
                          // TODO: We'll need to refactor AppBar and normalize it's across all screens
                          className={clsx({
                            [s.white]: tabsNavigation || subtitle,
                            [s.divider]: subtitle && !tabsNavigation,
                          })}
                          cover={cover}
                          tabNavigationStyles={tabNavigationStyles}
                        />
                      </>
                    )}
                    {filters && filters}
                  </>
                )}
                {children}

                {/* Render Tawk messenger only if `shouldShowHelpCenter` is true to avoid always-visible widget */}
                {shouldShowHelpCenter && (
                  <TawkMessengerReact
                    propertyId={talkToConfig.propertyId}
                    widgetId={talkToConfig.widgetId}
                  />
                )}

                {user?.role === UserRole.COACH && getAnyHelpModal && (
                  <GetAnyHelpModal
                    {...getAnyHelpModal}
                    onClose={() =>
                      setGetAnyHelpModal((prev) => ({
                        ...prev,
                        open: false,
                      }))
                    }
                  />
                )}
              </GetAnyHelpModalContext.Provider>
            </PreviewContext.Provider>
          </MinimizedDrawerContext.Provider>
        </AppDrawerContext.Provider>
      </BannersContext.Provider>
    </JourneyAvailabilityContext.Provider>
  );
}

export default AppLayout;
